/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { StatusBadge, PriorityBadge } from "../components/StatusBadge";
import { Plus, Trash2, Settings2, Check, X, Pencil, Save, Upload } from "lucide-react";
import { fmtDateOnlyIST } from "../lib/formatDateTime";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import PeopleTagger, { PeoplePills } from "../components/PeopleTagger";
import BulkTasksModal from "../components/BulkTasksModal";
import Pagination from "../components/Pagination";

// DEMO placeholder — actual match id now comes from useMatch() at runtime
const STATUS_OPTS = ["pending", "in_progress", "completed", "blocked"];
const PRIO_OPTS = ["critical", "high", "medium", "low"];
const LS_RENAMES = "mc_dept_renames_v1";

export default function PreMatchTrackerPage() {
  const { selectedMatchId } = useMatch();
  const DEMO = selectedMatchId;

  const { user: me } = useAuth();
  const isAdmin = (me?.permissions || []).includes("admin.users");
  const isDeptHead = me?.role === "department_head";
  const isVendor = me?.role === "vendor";
  const vendorDepts = isVendor ? (me?.vendor_departments || []) : [];
  const [tasks, setTasks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [customDepts, setCustomDepts] = useState([]);
  const [renames, setRenames] = useState({}); // { originalName: displayName }
  const [manageMode, setManageMode] = useState(false);
  const [editingDept, setEditingDept] = useState(null); // key currently being renamed
  const [renameDraft, setRenameDraft] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const deptFromUrl = searchParams.get("dept");
  const initialVendorDept = isVendor ? (vendorDepts[0] || "all") : null;
  const [filter, setFilter] = useState(
    initialVendorDept || deptFromUrl || (isDeptHead && me?.department ? me.department : "all")
  );

  // Sync filter when URL dept param changes (e.g., deep-link from Ground Command Grid)
  useEffect(() => {
    if (deptFromUrl && deptFromUrl !== filter) {
      setFilter(deptFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptFromUrl]);
  const [showNew, setShowNew] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [addingDept, setAddingDept] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");

  const [form, setForm] = useState({
    title: "",
    department: "",
    priority: "medium",
    status: "pending",
    due_date: "",
    description: "",
    tagged_people: [],
  });
  const [editingTask, setEditingTask] = useState(null); // task object being edited
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;

  const load = async () => {
    if (!DEMO) return;
    const [d, t] = await Promise.all([
      api.get(`/reference/pre-match-departments?match_id=${DEMO}`),
      api.get(`/matches/${DEMO}/pre-match-tasks`),
    ]);
    const savedRenames = JSON.parse(localStorage.getItem(LS_RENAMES) || "{}");
    // One-time migration: push any old localStorage custom depts to the backend, then clear.
    const legacy = JSON.parse(localStorage.getItem("mc_custom_depts") || "[]");
    const backendCustom = d.data.custom || [];
    const legacyNotOnServer = legacy.filter((x) => !backendCustom.includes(x) && !(d.data.base || []).includes(x));
    if (legacyNotOnServer.length) {
      for (const name of legacyNotOnServer) {
        try { await api.post(`/matches/${DEMO}/custom-departments`, { name }); } catch (_) {}
      }
      localStorage.removeItem("mc_custom_depts");
      // Re-fetch merged list now that server is authoritative
      const d2 = await api.get(`/reference/pre-match-departments?match_id=${DEMO}`);
      setDepartments(d2.data.departments);
      setCustomDepts(d2.data.custom || []);
    } else {
      setDepartments(d.data.departments);
      setCustomDepts(backendCustom);
    }
    const firstAlpha = [...d.data.departments].sort((a, b) => a.localeCompare(b))[0] || "";
    setRenames(savedRenames);
    setTasks(t.data);
    setForm((f) => (f.department ? f : { ...f, department: firstAlpha }));
  };
  useEffect(() => { load(); }, [DEMO]);

  // Display name = admin rename override if present, otherwise the original.
  const displayName = (key) => renames[key] || key;

  const persistRenames = (next) => {
    setRenames(next);
    localStorage.setItem(LS_RENAMES, JSON.stringify(next));
  };

  const beginRename = (key) => {
    setEditingDept(key);
    setRenameDraft(displayName(key));
  };

  const saveRename = () => {
    if (!editingDept) return;
    const val = renameDraft.trim();
    const next = { ...renames };
    if (!val || val === editingDept) {
      delete next[editingDept];
    } else {
      next[editingDept] = val;
    }
    persistRenames(next);
    setEditingDept(null);
    setRenameDraft("");
  };

  const cancelRename = () => {
    setEditingDept(null);
    setRenameDraft("");
  };

  const addDept = async () => {
    const name = newDeptName.trim();
    if (!name) return;
    try {
      const r = await api.post(`/matches/${DEMO}/custom-departments`, { name });
      const nextCustom = r.data.custom || [];
      setCustomDepts(nextCustom);
      setDepartments((prev) => Array.from(new Set([...prev, name])));
      setNewDeptName("");
      setAddingDept(false);
      setForm((f) => ({ ...f, department: name }));
    } catch (e) {
      alert(e.response?.data?.detail || "Could not add department.");
    }
  };

  const removeDept = async (name) => {
    const taskCount = tasks.filter((t) => t.department === name).length;
    const isCustom = customDepts.includes(name);
    const kind = isCustom ? "custom department" : "seeded department";
    const msg = taskCount > 0
      ? `Remove ${kind} "${name}"?\n\n${taskCount} existing task${taskCount > 1 ? "s" : ""} routed to this department will remain in the database but stop appearing under its filter chip.${!isCustom ? "\n\nThis only hides the department on THIS match — other matches are unaffected." : ""}`
      : `Remove ${kind} "${name}"? It has no tasks.${!isCustom ? "\n\nThis only hides the department on THIS match — other matches are unaffected." : ""}`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/matches/${DEMO}/custom-departments/${encodeURIComponent(name)}`);
      if (isCustom) {
        setCustomDepts((c) => c.filter((d) => d !== name));
      }
      setDepartments((d) => d.filter((x) => x !== name));
      if (renames[name]) {
        const nextR = { ...renames }; delete nextR[name];
        persistRenames(nextR);
      }
      if (filter === name) setFilter("all");
      if (form.department === name) {
        setForm((f) => ({ ...f, department: departments.find((d) => d !== name) || "" }));
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Could not remove department.");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    const payload = {
      department: form.department,
      title: form.title,
      description: form.description,
      priority: form.priority,
      status: form.status,
      due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
      tagged_people: form.tagged_people || [],
    };
    await api.post(`/matches/${DEMO}/pre-match-tasks`, payload);
    setForm({ ...form, title: "", description: "", due_date: "", tagged_people: [] });
    setShowNew(false);
    load();
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/pre-match-tasks/${id}`, { status });
    load();
  };

  const deleteTask = async (id) => {
    if (!window.confirm("Delete this to-do?")) return;
    await api.delete(`/pre-match-tasks/${id}`);
    load();
  };

  const openEdit = (t) => {
    setEditingTask(t);
    setEditForm({
      title: t.title || "",
      department: t.department || "",
      priority: t.priority || "medium",
      status: t.status || "pending",
      due_date: t.due_date ? new Date(t.due_date).toISOString().slice(0, 10) : "",
      description: t.description || "",
      tagged_people: t.tagged_people || [],
    });
  };
  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editingTask) return;
    setSavingEdit(true);
    try {
      await api.patch(`/pre-match-tasks/${editingTask.id}`, {
        title: editForm.title,
        department: editForm.department,
        priority: editForm.priority,
        status: editForm.status,
        due_date: editForm.due_date ? new Date(editForm.due_date).toISOString() : null,
        description: editForm.description,
        tagged_people: editForm.tagged_people,
      });
      setEditingTask(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update task");
    } finally {
      setSavingEdit(false);
    }
  };

  const filtered = tasks.filter((t) =>
    filter === "all" ? true :
    filter === "open" ? t.status !== "completed" :
    filter === "critical" ? (t.priority === "critical" && t.status !== "completed") :
    t.department === filter
  );

  // Reset to page 1 whenever the filter changes so users don't land on an
  // empty page after narrowing the list.
  useEffect(() => { setPage(1); }, [filter]);
  const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // Always render department lists alphabetically (both filter chips and form dropdown)
  const sortedDepartments = [...departments].sort((a, b) => a.localeCompare(b));

  const totalDone = tasks.filter((t) => t.status === "completed").length;

  if (!selectedMatchId) return <RequireMatch />;
  return (
    <div className="p-6 md:p-10 pb-20" data-testid="page-pre-match-tracker">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Pre-Match · Department To-Do Timelines</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Pre-Match Tracker</h1>
          <p className="text-zinc-400 mt-2 text-sm max-w-2xl">Every pre-match department has its own to-do list with timelines. Filter by department, mark status.</p>
          {isDeptHead && me?.department && filter === me.department && (
            <div className="mt-2 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-readyGreen border border-readyGreen/50 px-2 py-1" data-testid="pm-dept-scope-hint">
              Scoped to your department · {me.department}
              <button
                onClick={() => setFilter("all")}
                className="ml-2 text-zinc-400 hover:text-white normal-case tracking-normal text-[10px] underline"
                data-testid="pm-dept-scope-clear"
              >
                view all
              </button>
            </div>
          )}
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="mc-panel p-3 text-right">
            <div className="mc-label">Overall</div>
            <div className="mc-metric text-3xl mt-1" style={{ color: "#34C759" }}>{totalDone}/{tasks.length}</div>
          </div>
          {isAdmin && !isVendor && (
            <button
              onClick={() => { setManageMode((v) => !v); setEditingDept(null); }}
              className={`flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest border transition-colors ${
                manageMode
                  ? "bg-warnAmber text-black border-warnAmber"
                  : "border-cmdBorder text-zinc-400 hover:border-white hover:text-white"
              }`}
              data-testid="pm-manage-depts-btn"
              title="Rename departments (admin)"
            >
              <Settings2 className="w-3 h-3" /> {manageMode ? "Done" : "Manage Depts"}
            </button>
          )}
          {!isVendor && (
            <button
              onClick={() => setShowBulk(true)}
              data-testid="pm-task-bulk-btn"
              className="border border-cmdBorder hover:border-white text-white px-3 py-2 text-xs uppercase tracking-widest font-bold flex items-center gap-1 transition-colors"
              title="Bulk-import tasks from CSV or Excel paste"
            >
              <Upload className="w-3 h-3" /> Bulk Import
            </button>
          )}
          {!isVendor && (
            <button
              onClick={() => setShowNew((v) => !v)}
              data-testid="pm-task-new-btn"
              className="bg-white text-black px-4 py-2 font-bold uppercase tracking-widest text-xs hover:bg-zinc-200 transition-colors"
            >
              {showNew ? "Cancel" : "+ New Task"}
            </button>
          )}
          {isVendor && (
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-readyGreen border border-readyGreen/50 px-2 py-2" data-testid="pm-vendor-hint">
              Vendor · view & complete only
            </div>
          )}
        </div>
      </div>

      {showNew && (
        <form onSubmit={create} className="mc-panel p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="pm-task-form">
          <input
            className="bg-black border border-cmdBorder p-2 text-sm md:col-span-2"
            placeholder="Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            data-testid="pm-task-form-title"
          />
          <select
            className="bg-black border border-cmdBorder p-2 text-sm"
            value={form.department}
            onChange={(e) => setForm({ ...form, department: e.target.value })}
            data-testid="pm-task-form-dept"
          >
            {sortedDepartments.map((d) => <option key={d} value={d}>{displayName(d)}</option>)}
          </select>
          <select
            className="bg-black border border-cmdBorder p-2 text-sm"
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value })}
            data-testid="pm-task-form-prio"
          >
            {PRIO_OPTS.map((p) => <option key={p}>{p}</option>)}
          </select>
          <input
            type="date"
            className="bg-black border border-cmdBorder p-2 text-sm"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
          />
          <input
            className="bg-black border border-cmdBorder p-2 text-sm md:col-span-2"
            placeholder="Description (optional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="pm-task-form-desc"
          />
          <button type="submit" className="bg-readyGreen text-black py-2 font-bold uppercase tracking-widest text-xs" data-testid="pm-task-form-submit">Create</button>
          <div className="md:col-span-4">
            <label className="mc-label">Tag Individuals (optional)</label>
            <div className="mt-1">
              <PeopleTagger
                value={form.tagged_people}
                onChange={(v) => setForm({ ...form, tagged_people: v })}
                placeholder="Add names who own this task…"
                testidPrefix="pm-tagger"
              />
            </div>
          </div>
        </form>
      )}

      {/* Filters — All / Open / Critical then per-department (desktop chips / mobile dropdown) */}
      <div className="mb-4" data-testid="pm-task-filters">
        <div className="flex flex-wrap gap-2 items-center">
          {(isVendor ? [] : [["all", "All"], ["open", "Open"], ["critical", "Critical"]]).map(([k, l]) => {
            const isCritical = k === "critical";
            const isOpen = k === "open";
            const isActive = filter === k;
            const inactiveCls = isCritical
              ? "border-incidentRed/50 text-incidentRed hover:border-incidentRed hover:bg-incidentRed/10"
              : isOpen
                ? "border-orange-400/50 text-orange-400 hover:border-orange-400 hover:bg-orange-400/10"
                : "border-cmdBorder text-zinc-400 hover:border-white";
            const activeCls = isCritical
              ? "border-incidentRed bg-incidentRed text-white"
              : isOpen
                ? "border-orange-400 bg-orange-400 text-black"
                : "border-white bg-white text-black";
            return (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`pm-task-filter-${k}`}
                className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors ${
                  isActive ? activeCls : inactiveCls
                }`}
              >
                {l}
              </button>
            );
          })}
          <div className="ml-auto font-mono-live text-xs text-zinc-500 self-center">
            {filtered.length} of {tasks.length}
          </div>
        </div>

        {/* Mobile: department dropdown + Dept add — replaces the wall of chips */}
        <div className="md:hidden flex items-center gap-2 mt-3" data-testid="pm-task-filters-mobile">
          <select
            className={`flex-1 bg-black border p-2 text-xs ${
              sortedDepartments.includes(filter) ? "border-white text-white" : "border-cmdBorder text-zinc-300"
            }`}
            value={sortedDepartments.includes(filter) ? filter : "all"}
            onChange={(e) => setFilter(e.target.value === "all" ? "all" : e.target.value)}
            data-testid="pm-task-filter-dept-mobile"
          >
            <option value="all">All departments</option>
            {(isVendor ? sortedDepartments.filter((d) => vendorDepts.includes(d)) : sortedDepartments).map((d) => (
              <option key={d} value={d}>{displayName(d)}</option>
            ))}
          </select>
          {!isVendor && (addingDept ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                className="bg-black border border-cmdBorder p-2 text-xs w-28"
                placeholder="New dept"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addDept()}
                data-testid="new-dept-input-mobile"
              />
              <button onClick={addDept} className="bg-white text-black px-2 py-2 text-[10px]" data-testid="new-dept-save-mobile"><Check className="w-3 h-3" /></button>
              <button onClick={() => setAddingDept(false)} className="border border-cmdBorder px-2 py-2 text-[10px]"><X className="w-3 h-3" /></button>
            </div>
          ) : (
            <button
              onClick={() => setAddingDept(true)}
              className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold border border-dashed border-cmdBorder text-zinc-400 hover:border-white hover:text-white px-3 py-2"
              data-testid="new-dept-btn-mobile"
            >
              <Plus className="w-3 h-3" /> Dept
            </button>
          ))}
        </div>

        {/* Desktop: department chips row */}
        <div className="hidden md:flex flex-wrap gap-2 mt-2">
          <div className="h-6 w-px bg-cmdBorder mx-1 self-center" />
          {(isVendor ? sortedDepartments.filter((d) => vendorDepts.includes(d)) : sortedDepartments).map((d) => {
            if (manageMode && editingDept === d) {
              return (
                <div key={d} className="flex items-center gap-1" data-testid={`pm-rename-editor-${d}`}>
                  <input
                    autoFocus
                    className="bg-black border border-warnAmber text-warnAmber p-1.5 text-xs w-44 uppercase tracking-widest"
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveRename();
                      if (e.key === "Escape") cancelRename();
                    }}
                    data-testid={`pm-rename-input-${d}`}
                  />
                  <button onClick={saveRename} className="bg-warnAmber text-black px-2 py-1.5 text-[10px]" title="Save" data-testid={`pm-rename-save-${d}`}>
                    <Check className="w-3 h-3" />
                  </button>
                  <button onClick={cancelRename} className="border border-cmdBorder px-2 py-1.5 text-[10px]" title="Cancel" data-testid={`pm-rename-cancel-${d}`}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            }
            if (manageMode) {
              const isCustom = customDepts.includes(d);
              return (
                <div key={d} className="flex items-center" data-testid={`pm-manage-chip-${d}`}>
                  <button
                    onClick={() => beginRename(d)}
                    data-testid={`pm-rename-btn-${d}`}
                    title={renames[d] ? `Original: ${d}` : "Rename department"}
                    className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border border-warnAmber/60 border-r-0 text-warnAmber hover:bg-warnAmber hover:text-black transition-colors"
                  >
                    {displayName(d)}
                    <Settings2 className="w-3 h-3 opacity-70" />
                  </button>
                  <button
                    onClick={() => removeDept(d)}
                    data-testid={`pm-delete-dept-${d}`}
                    title={isCustom ? `Delete custom department "${d}"` : `Hide "${d}" on this match`}
                    className="flex items-center px-2 py-1.5 border border-warnAmber/60 border-l-0 text-incidentRed hover:bg-incidentRed hover:text-white transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            }
            return (
              <button
                key={d}
                onClick={() => setFilter(d)}
                data-testid={`pm-task-filter-${d}`}
                className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors ${
                  filter === d ? "border-white bg-white text-black" : "border-cmdBorder text-zinc-400 hover:border-white"
                }`}
              >
                {displayName(d)}
              </button>
            );
          })}
          {isVendor ? null : addingDept ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                className="bg-black border border-cmdBorder p-1.5 text-xs w-40"
                placeholder="New department"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addDept()}
                data-testid="new-dept-input"
              />
              <button onClick={addDept} className="bg-white text-black px-2 py-1.5 text-[10px] uppercase tracking-widest font-bold" data-testid="new-dept-save">Add</button>
              <button onClick={() => setAddingDept(false)} className="border border-cmdBorder px-2 py-1.5 text-[10px] uppercase tracking-widest">×</button>
            </div>
          ) : (
            <button
              onClick={() => setAddingDept(true)}
              className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold border border-dashed border-cmdBorder text-zinc-400 hover:border-white hover:text-white px-3 py-1.5 transition-colors"
              data-testid="new-dept-btn"
            >
              <Plus className="w-3 h-3" /> Dept
            </button>
          )}
        </div>
      </div>

      <div className="mc-panel overflow-x-auto hidden md:block">
        <table className="w-full mc-table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Dept</th>
              <th>Due</th>
              <th>Priority</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((t) => (
              <tr key={t.id} data-testid={`pm-task-row-${t.id}`}>
                <td>
                  <div className="font-medium">{t.title}</div>
                  {t.description && <div className="text-xs text-zinc-500 mt-0.5">{t.description}</div>}
                  <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
                    <span className="mc-label">Dept</span>
                    <span className="text-white font-medium">{displayName(t.department)}</span>
                    {t.tagged_people?.length > 0 && (
                      <>
                        <span className="text-zinc-600">·</span>
                        <PeoplePills people={t.tagged_people} testidPrefix={`pm-tagged-${t.id}`} />
                      </>
                    )}
                  </div>
                </td>
                <td className="text-zinc-300">{displayName(t.department)}</td>
                <td className="text-zinc-400 font-mono-live text-xs">
                  {fmtDateOnlyIST(t.due_date)}
                </td>
                <td><PriorityBadge priority={t.priority} done={t.status === "completed"} /></td>
                <td><StatusBadge status={t.status} kind="pm-task" live={t.status === "in_progress"} /></td>
                <td className="text-right">
                  <div className="inline-flex gap-2 justify-end">
                    {isVendor ? (
                      t.status !== "completed" ? (
                        <button
                          onClick={() => updateStatus(t.id, "completed")}
                          className="border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-white px-3 py-1 text-xs uppercase tracking-widest transition-colors"
                          data-testid={`pm-vendor-complete-${t.id}`}
                        >
                          Mark Completed
                        </button>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-readyGreen border border-readyGreen/50 px-2 py-1" data-testid={`pm-vendor-done-${t.id}`}>
                          Completed
                        </span>
                      )
                    ) : (
                      <>
                        <select
                          className="bg-black border border-cmdBorder text-xs py-1 px-2"
                          value={t.status}
                          onChange={(e) => updateStatus(t.id, e.target.value)}
                          data-testid={`pm-task-status-select-${t.id}`}
                        >
                          {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                        </select>
                        <button
                          onClick={() => openEdit(t)}
                          className="border border-cmdBorder hover:border-white px-2 py-1 text-xs transition-colors"
                          data-testid={`pm-edit-${t.id}`}
                          title="Edit task"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => deleteTask(t.id)}
                          className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-2 py-1 text-xs transition-colors"
                          data-testid={`pm-delete-${t.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 py-8">No tasks match this filter.</td></tr>}
          </tbody>
        </table>
        <div className="px-4 pb-3">
          <Pagination
            page={page}
            perPage={PER_PAGE}
            total={filtered.length}
            onPageChange={setPage}
            testidPrefix="pm-task-pagination"
          />
        </div>
      </div>

      {/* Mobile: card list — replaces the wide table on small screens */}
      <div className="md:hidden space-y-3" data-testid="pm-task-mobile-list">
        {filtered.length === 0 && (
          <div className="mc-panel p-6 text-center text-zinc-500 text-sm">No tasks match this filter.</div>
        )}
        {pageItems.map((t) => (
          <div key={t.id} className="mc-panel p-4" data-testid={`pm-task-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{t.title}</div>
                {t.description && <div className="text-xs text-zinc-500 mt-1">{t.description}</div>}
              </div>
              <StatusBadge status={t.status} kind="pm-task" live={t.status === "in_progress"} />
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px]">
              <span className="border border-cmdBorder text-zinc-300 px-2 py-1 uppercase tracking-widest">{displayName(t.department)}</span>
              <PriorityBadge priority={t.priority} done={t.status === "completed"} />
              <span className="text-zinc-500 font-mono-live ml-auto">{fmtDateOnlyIST(t.due_date)}</span>
            </div>
            {t.tagged_people?.length > 0 && (
              <div className="mt-2"><PeoplePills people={t.tagged_people} testidPrefix={`pm-tagged-m-${t.id}`} /></div>
            )}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {isVendor ? (
                t.status !== "completed" ? (
                  <button
                    onClick={() => updateStatus(t.id, "completed")}
                    className="flex-1 border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-white py-2.5 text-xs uppercase tracking-widest transition-colors"
                    data-testid={`pm-vendor-complete-m-${t.id}`}
                  >
                    Mark Completed
                  </button>
                ) : (
                  <span className="flex-1 text-center text-[10px] uppercase tracking-widest text-readyGreen border border-readyGreen/50 py-2.5" data-testid={`pm-vendor-done-m-${t.id}`}>
                    Completed
                  </span>
                )
              ) : (
                <>
                  <select
                    className="flex-1 bg-black border border-cmdBorder text-xs py-2.5 px-2"
                    value={t.status}
                    onChange={(e) => updateStatus(t.id, e.target.value)}
                    data-testid={`pm-task-status-select-m-${t.id}`}
                  >
                    {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                  <button
                    onClick={() => openEdit(t)}
                    className="border border-cmdBorder hover:border-white h-10 w-10 flex items-center justify-center text-xs"
                    data-testid={`pm-edit-m-${t.id}`}
                    title="Edit task"
                    aria-label="Edit task"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteTask(t.id)}
                    className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed h-10 w-10 flex items-center justify-center text-xs"
                    data-testid={`pm-delete-m-${t.id}`}
                    aria-label="Delete task"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        <Pagination
          page={page}
          perPage={PER_PAGE}
          total={filtered.length}
          onPageChange={setPage}
          testidPrefix="pm-task-pagination-mobile"
        />
      </div>

      {/* Edit task modal */}
      {editingTask && editForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditingTask(null)} data-testid="pm-edit-modal">
          <form
            onSubmit={submitEdit}
            className="mc-panel p-5 max-w-2xl w-full space-y-3 max-h-[85vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="mc-label">Edit Task</div>
                <h3 className="font-head font-bold text-2xl uppercase mt-1">{editingTask.title}</h3>
              </div>
              <button type="button" onClick={() => setEditingTask(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="mc-label">Title</label>
                <input required className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  data-testid="pm-edit-title" />
              </div>
              <div>
                <label className="mc-label">Department</label>
                <select className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                  value={editForm.department}
                  onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                  data-testid="pm-edit-dept">
                  {departments.map((d) => <option key={d} value={d}>{displayName(d)}</option>)}
                </select>
              </div>
              <div>
                <label className="mc-label">Due date</label>
                <input type="date" className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1 font-mono-live"
                  value={editForm.due_date}
                  onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                  data-testid="pm-edit-due" />
              </div>
              <div>
                <label className="mc-label">Priority</label>
                <select className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                  value={editForm.priority}
                  onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                  data-testid="pm-edit-priority">
                  {PRIO_OPTS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="mc-label">Status</label>
                <select className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  data-testid="pm-edit-status">
                  {STATUS_OPTS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="mc-label">Description</label>
                <textarea rows={3} className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  data-testid="pm-edit-desc" />
              </div>
              <div className="md:col-span-2">
                <label className="mc-label">Tagged people</label>
                <div className="mt-1">
                  <PeopleTagger
                    value={editForm.tagged_people}
                    onChange={(v) => setEditForm({ ...editForm, tagged_people: v })}
                    placeholder="Tag responsible people…"
                    testidPrefix="pm-edit-tagger"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="submit" disabled={savingEdit}
                className="flex-1 flex items-center justify-center gap-1 bg-readyGreen text-white py-2 font-bold uppercase tracking-widest text-xs disabled:opacity-60"
                data-testid="pm-edit-submit">
                <Save className="w-3 h-3" /> {savingEdit ? "Saving…" : "Save Changes"}
              </button>
              <button type="button" onClick={() => setEditingTask(null)} className="border border-cmdBorder px-4 py-2 text-xs uppercase tracking-widest">Cancel</button>
            </div>
          </form>
        </div>
      )}
      <BulkTasksModal
        open={showBulk}
        onClose={() => setShowBulk(false)}
        onImported={load}
        matchId={DEMO}
      />
    </div>
  );
}
